import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { Target, LogIn, UserPlus, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [venueName, setVenueName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPendingMessage("");
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const body = isRegister ? { username, password, email, venue_name: venueName } : { username, password };
      const res = await axios.post(`${API}${endpoint}`, body);
      if (isRegister && res.data.status === "pending") {
        setPendingMessage(res.data.message);
        toast.info("Registration submitted!");
        setIsRegister(false);
        return;
      }
      localStorage.setItem("darts_token", res.data.token);
      localStorage.setItem("darts_username", res.data.username);
      localStorage.setItem("darts_role", res.data.role || "admin");
      localStorage.setItem("darts_venue_id", res.data.venue_id || "");
      localStorage.setItem("darts_venue_name", res.data.venue_name || "");
      toast.success(`Welcome, ${res.data.username}!`);
      navigate("/admin");
    } catch (e) {
      const detail = e.response?.data?.detail || "Authentication failed";
      toast.error(detail);
      if (detail.includes("awaiting approval")) {
        setPendingMessage(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] relative"
      style={{
        backgroundImage:
          "url(https://static.prod-images.emergentagent.com/jobs/f707a822-bbe1-4c69-96b7-9259b8de4d00/images/fbdf8f044500ad2fe4d88a49bf647ff10fc9a081bcebea33ee50a27fb0f33e44.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8 animate-fade-in">
            <Target className="w-12 h-12 text-[#E3292E] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-display text-3xl text-white uppercase tracking-tighter">Admin Access</h1>
          </div>

          <Card className="bg-[#141414]/90 backdrop-blur-sm border-white/10 rounded-none animate-slide-up">
            <CardHeader>
              <CardTitle className="font-heading text-white text-xl">
                {isRegister ? "Create Account" : "Login"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingMessage && (
                <div className="mb-4 p-3 border border-yellow-500/20 bg-yellow-500/5" data-testid="pending-message">
                  <p className="text-yellow-400 text-sm font-body">{pendingMessage}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div className="space-y-2">
                    <Label htmlFor="venue" className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Venue Name</Label>
                    <Input id="venue" value={venueName} onChange={(e) => setVenueName(e.target.value)} className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]" placeholder="e.g. Joe's Pub Darts" required data-testid="venue-name-input" />
                  </div>
                )}
                {isRegister && (
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]" placeholder="your@email.com" required data-testid="email-input" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">
                    Username
                  </Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E] focus-visible:border-[#E3292E]"
                    placeholder="Enter username"
                    required
                    data-testid="username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E] focus-visible:border-[#E3292E]"
                    placeholder="Enter password"
                    required
                    data-testid="password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none py-5 font-heading font-bold tracking-wider transition-colors duration-200"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    "Please wait..."
                  ) : isRegister ? (
                    <>
                      <UserPlus className="mr-2 h-4 w-4" /> CREATE ACCOUNT
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" /> LOGIN
                    </>
                  )}
                </Button>
              </form>
              <div className="mt-4 text-center space-y-2">
                {!isRegister && (
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(""); }}
                    className="text-[#E3292E]/60 hover:text-[#E3292E] text-xs transition-colors duration-200"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                )}
                <button
                  onClick={() => setIsRegister(!isRegister)}
                  className="text-white/40 hover:text-white text-sm transition-colors duration-200 block mx-auto"
                  data-testid="toggle-register-btn"
                >
                  {isRegister ? "Already have an account? Login" : "First time? Create account"}
                </button>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="w-full mt-4 text-white/40 hover:text-white hover:bg-white/5 rounded-none transition-colors duration-200"
            data-testid="back-home-btn"
          >
            Back to Home
          </Button>
        </div>

        {/* Forgot Password Dialog */}
        <Dialog open={showForgot} onOpenChange={setShowForgot}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-white text-xl flex items-center gap-2">
                <Mail className="h-5 w-5 text-[#E3292E]" /> Reset Password
              </DialogTitle>
            </DialogHeader>
            {forgotSent ? (
              <div className="space-y-4 text-center py-4">
                <div className="w-12 h-12 bg-[#309F6A]/10 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-6 w-6 text-[#309F6A]" />
                </div>
                <p className="text-white font-heading font-bold">Check Your Email</p>
                <p className="text-white/50 text-sm font-body">If an account exists for <strong className="text-white">{forgotEmail}</strong>, a password reset link has been sent. The link expires in 1 hour.</p>
                <Button onClick={() => setShowForgot(false)} className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading" data-testid="forgot-close-btn">
                  BACK TO LOGIN
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-white/50 text-sm font-body">Enter your account email and we'll send you a link to reset your password.</p>
                <div className="space-y-2">
                  <Label className="text-white/40 text-xs uppercase tracking-wider">Email Address</Label>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading || !forgotEmail.trim()}
                  className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? "Sending..." : "SEND RESET LINK"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
